import React from 'react';

import styles from './Card.module.scss';

export const Card = ({
  icon,
  title,
  subtitle,
  footer,
  children,
  colClasses = 'col-12',
  cardClasses = 'shadow-sm',
  actions, // ⟵ header actions support
  // -- Collapsible API (retro-compat)
  collapsible = true,
  defaultExpanded = true,
  expanded,
  onToggle,
  headerToggles = true,
  transitionMs = 200, // durata animazione
  // -- Nuove prop allineate a CollapsibleHeader
  expandedHeight = 'auto', // "auto" | number | string ("60vh", "32rem", ecc.)
  collapsedHeight = 0, // number | string
}) => {
  const outerClass = (colClasses ?? '').trim();
  const isControlled = typeof expanded === 'boolean';
  const [openInternal, setOpenInternal] = React.useState(defaultExpanded);
  const open = isControlled ? expanded : openInternal;

  const bodyIdRef = React.useRef(`card_body_${Math.random().toString(36).slice(2, 10)}`);
  const bodyRef = React.useRef(null);

  // "hidden" during animation / closed; "visible" once fully open.
  // overflow:visible is required so absolutely-positioned dropdowns inside
  // cards (e.g. CustomerPicker results) are never clipped.
  const [bodyOverflow, setBodyOverflow] = React.useState('hidden');

  const prefersReduced =
    typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // ---- helpers height "like CollapsibleHeader"
  const toCssHeight = (v) => {
    if (v === 'auto') return 'auto';
    if (typeof v === 'number') return `${v}px`;
    return v; // string con unità
  };

  const setHeight = React.useCallback((value) => {
    const el = bodyRef.current;
    if (!el) return;
    el.style.height = toCssHeight(value);
  }, []);

  const getExpandedTarget = React.useCallback(() => {
    if (expandedHeight === 'auto') {
      const el = bodyRef.current;
      if (!el) return 'auto';
      return el.scrollHeight; // numero -> px
    }
    return expandedHeight;
  }, [expandedHeight]);

  const setOpen = React.useCallback(
    (next) => {
      if (!isControlled) setOpenInternal(next);
      onToggle?.(next);
    },
    [isControlled, onToggle]
  );

  const toggle = React.useCallback(() => {
    if (!collapsible) return;
    setOpen(!open);
  }, [collapsible, open]);

  // ---- animazione apertura/chiusura (height + auto)
  React.useEffect(() => {
    if (!collapsible) return;
    const el = bodyRef.current;
    if (!el) return;

    if (prefersReduced) {
      setHeight(open ? expandedHeight : collapsedHeight);
      setBodyOverflow(open ? 'visible' : 'hidden');
      return;
    }

    // Clip content during every animation (open or close).
    setBodyOverflow('hidden');

    if (open) {
      setHeight(collapsedHeight);
      requestAnimationFrame(() => {
        const target = getExpandedTarget();
        setHeight(target);
      });
    } else {
      const current = getComputedStyle(el).height;
      if (current === 'auto') {
        setHeight(el.scrollHeight); // punto di partenza numerico
        // forza reflow
        // eslint-disable-next-line no-unused-expressions
        el.offsetHeight;
      }
      setHeight(collapsedHeight);
    }
  }, [
    open,
    collapsible,
    prefersReduced,
    expandedHeight,
    collapsedHeight,
    getExpandedTarget,
    setHeight,
  ]);

  // A fine transizione, se aperto e expandedHeight === "auto" => height:auto
  React.useEffect(() => {
    const el = bodyRef.current;
    if (!el || prefersReduced) return;
    const onEnd = (e) => {
      if (e.propertyName !== 'height') return;
      if (open && expandedHeight === 'auto') {
        setHeight('auto');
      }
      // Restore overflow:visible so dropdowns inside the card are not clipped.
      setBodyOverflow(open ? 'visible' : 'hidden');
    };
    el.addEventListener('transitionend', onEnd);
    return () => el.removeEventListener('transitionend', onEnd);
  }, [open, prefersReduced, expandedHeight, setHeight]);

  // Sincronizza quando cambia il contenuto
  React.useEffect(() => {
    const el = bodyRef.current;
    if (!el || !collapsible) return; // Aggiunto !collapsible

    if (prefersReduced) {
      setHeight(open ? expandedHeight : collapsedHeight);
      return;
    }

    if (open) {
      if (expandedHeight === 'auto') {
        // If already at height:auto, let the browser handle content changes naturally.
        // Setting a fixed px here would race against async ResizeObserver updates
        // inside children (e.g. CustomerPicker paddingBottom), causing the body to
        // lock at a stale height and clip absolutely-positioned dropdowns.
        if (el.style.height === 'auto') return;
        const targetHeight = el.scrollHeight;
        setHeight(targetHeight);
      } else {
        setHeight(expandedHeight);
      }
    } else {
      setHeight(collapsedHeight);
    }
  }, [children, open, collapsible, prefersReduced, expandedHeight, collapsedHeight, setHeight]);

  const bodyId = bodyIdRef.current;

  // Altezza iniziale per evitare flash
  const initialHeight = open
    ? toCssHeight(expandedHeight === 'auto' ? 'auto' : expandedHeight)
    : toCssHeight(collapsedHeight);

  // Evita che i click sugli actions inneschino il toggle dell'header
  const stopHeaderToggle = (e) => {
    e.stopPropagation();
  };

  const renderTitle = (title) => {
    if (!title) return null;
    if (typeof title === 'string') return <h5 className={styles.title}>{title}</h5>;
    return title;
  };

  const cardEl = (
    <div className={`card ${styles.dsCard} ${cardClasses}`.trim()}>
      {(title || subtitle) && (
        <div
          className={`card-header d-flex align-items-center ${collapsible && headerToggles ? 'cursor-pointer' : ''}`}
          onClick={collapsible && headerToggles ? toggle : undefined}
          role={collapsible && headerToggles ? 'button' : undefined}
          aria-expanded={collapsible ? open : undefined}
          aria-controls={collapsible ? bodyId : undefined}
          style={{ gap: '0.5rem' }}
        >
          {/* LEFT: icon badge + title/subtitle */}
          <div className={styles.headerLeft}>
            {icon && (
              <span className={styles.headerIcon}>
                <i className={`fa-solid fa-${icon}`} aria-hidden="true" />
              </span>
            )}
            <div className={styles.headerText}>
              {title && renderTitle(title)}
              {subtitle && <small className={styles.subtitle}>{subtitle}</small>}
            </div>
          </div>

          {/* RIGHT: everything else (icon, subtitle, actions, chevron) */}
          <div
            className="d-inline-flex align-items-center ml-auto"
            style={{ gap: '0.75rem' }}
            onClick={stopHeaderToggle}
            onMouseDown={stopHeaderToggle}
            onKeyDown={stopHeaderToggle}
          >
            {actions && (
              <div className="card-actions d-inline-flex align-items-center">{actions}</div>
            )}

            {collapsible && (
              <button
                type="button"
                className="btn btn-link p-0"
                onClick={(e) => {
                  e.stopPropagation();
                  toggle();
                }}
                aria-label={open ? 'Comprimi' : 'Espandi'}
                aria-expanded={open}
                aria-controls={bodyId}
              >
                <i
                  className={`fa-solid fa-chevron-down ${styles.chevron}`}
                  aria-hidden="true"
                  aria-expanded={open}
                  style={{
                    transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: `transform ${transitionMs}ms ease`,
                  }}
                />
              </button>
            )}
          </div>
        </div>
      )}

      <div
        id={bodyId}
        ref={bodyRef}
        className={`card-body${collapsible ? '' : ' no-anim'}`}
        role="region"
        aria-label={typeof title === 'string' ? title : undefined}
        tabIndex={0}
        style={
          collapsible
            ? {
                height: initialHeight,
                overflow: bodyOverflow,
                transition: prefersReduced
                  ? 'none'
                  : `height ${transitionMs}ms ease, opacity ${transitionMs}ms ease`,
                opacity: (open ? expandedHeight : collapsedHeight) === 0 ? 0 : 1,
              }
            : undefined
        }
      >
        {children}
      </div>

      {footer && <div className="card-footer">{footer}</div>}
    </div>
  );

  return outerClass ? <div className={outerClass}>{cardEl}</div> : cardEl;
};
